import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import axios from "axios"

const API = process.env.REACT_APP_API_URL || "http://localhost:8000"

function authHeaders() {
    return { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
}

const EXCHANGES = ["binance", "bybit", "kraken", "coinbase", "okx"]

export default function Settings() {
    const nav = useNavigate()
    const [fees, setFees] = useState([])
    const [alerts, setAlerts] = useState({ email: true, telegram: false })
    const [telegramLinked, setTelegramLinked] = useState(false)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [message, setMessage] = useState("")

    const loadFees = () =>
        axios.get(`${API}/user/fees`, authHeaders())
            .then(res => res.data?.fees || [])
            .catch(() => [])

    const refreshAll = async () => {
        setLoading(true)
        const [feesRes, alertsRes, tgRes] = await Promise.all([
            loadFees(),
            axios.get(`${API}/user/alerts`, authHeaders()).catch(() => null),
            axios.get(`${API}/telegram/status`, authHeaders()).catch(() => null),
        ])
        setFees(feesRes)
        if (alertsRes?.data) setAlerts({
            email:    alertsRes.data.alerts_email ?? true,
            telegram: alertsRes.data.alerts_telegram ?? false,
        })
        if (tgRes?.data) setTelegramLinked(tgRes.data.linked)
        setLoading(false)
    }

    useEffect(() => { refreshAll() }, [])

    // FIX: store input values as raw strings while editing,
    // so deleting characters doesn't immediately turn into 0
    const updateFee = (exchange, feeType, rawValue) => {
        const newFees = fees.map(f => {
            if (f.exchange === exchange && f.fee_type === feeType) {
                return {
                    ...f,
                    fee_input: rawValue,                       // raw input string
                    fee_percent: rawValue === "" ? 0 : parseFloat(rawValue) || 0,
                    is_default: false,
                }
            }
            return f
        })
        setFees(newFees)
    }

    const resetFees = async () => {
        if (!window.confirm("Reset all fees to exchange defaults?")) return
        setSaving(true)
        try {
            await axios.delete(`${API}/user/fees`, authHeaders())
            // FIX: reload from server so we get actual default values, not zeros
            const fresh = await loadFees()
            setFees(fresh)
            setMessage("✓ Fees reset to defaults")
        } catch {
            setMessage("Could not reset fees")
        } finally {
            setSaving(false)
        }
    }

    const saveFees = async () => {
        setSaving(true); setMessage("")
        try {
            const customFees = fees
                .filter(f => !f.is_default)
                .map(f => ({
                    exchange:    f.exchange,
                    fee_type:    f.fee_type,
                    fee_percent: parseFloat(f.fee_percent) || 0,
                }))
            await axios.post(`${API}/user/fees`, { fees: customFees }, authHeaders())
            setMessage("✓ Fees saved")
            const fresh = await loadFees()
            setFees(fresh)
        } catch (err) {
            setMessage(err.response?.data?.detail || "Could not save fees")
        } finally {
            setSaving(false)
        }
    }

    const saveAlerts = async () => {
        setSaving(true); setMessage("")
        try {
            await axios.post(`${API}/user/alerts`, alerts, authHeaders())
            setMessage("✓ Alert preferences saved")
        } catch (err) {
            setMessage(err.response?.data?.detail || "Could not save")
        } finally {
            setSaving(false)
        }
    }

    const linkTelegram = async () => {
        try {
            const res = await axios.post(`${API}/telegram/link`, {}, authHeaders())
            if (res.data.url) window.open(res.data.url, "_blank")
        } catch (err) {
            setMessage(err.response?.data?.detail || "Could not generate Telegram link")
        }
    }

    const unlinkTelegram = async () => {
        if (!window.confirm("Unlink your Telegram?")) return
        try {
            await axios.post(`${API}/telegram/unlink`, {}, authHeaders())
            setTelegramLinked(false)
            setMessage("✓ Telegram unlinked")
        } catch {
            setMessage("Could not unlink")
        }
    }

    if (loading) {
        return <div style={{ padding: 40, color: "#cbd5e1", background: "#060b23", minHeight: "100vh" }}>Loading...</div>
    }

    // Group fees by exchange for the table
    const byExchange = {}
    fees.forEach(f => {
        if (!byExchange[f.exchange]) byExchange[f.exchange] = {}
        byExchange[f.exchange][f.fee_type] = f
    })

    // Render the value: prefer raw input string while user is typing
    const renderValue = (f) => {
        if (!f) return ""
        if (f.fee_input !== undefined) return f.fee_input
        return f.fee_percent
    }

    return (
        <div style={{ background: "#060b23", color: "#e8eaf0", minHeight: "100vh", padding: "32px" }}>
            <div style={{ maxWidth: 800, margin: "0 auto" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 32 }}>
                    <h1 style={{ color: "#00ffc6", margin: 0 }}>Settings</h1>
                    <button onClick={() => nav("/dashboard")} style={btn("secondary")}>← Back to Dashboard</button>
                </div>

                {message && (
                    <div style={{
                        padding: "10px 14px", borderRadius: 8, marginBottom: 20,
                        background: message.startsWith("✓") ? "rgba(0,255,198,0.1)" : "rgba(248,113,113,0.1)",
                        color: message.startsWith("✓") ? "#00ffc6" : "#f87171",
                    }}>{message}</div>
                )}

                <section style={card}>
                    <h2 style={h2}>Per-exchange fee overrides</h2>
                    <p style={hint}>
                        Default fees shown below are standard taker rates. If you have VIP or different rates,
                        type your own value and click Save. Click "Reset to defaults" to restore.
                    </p>

                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                        <thead>
                            <tr>
                                <th style={th}>Exchange</th>
                                <th style={th}>Trading fee %</th>
                                <th style={th}>Withdrawal fee %</th>
                            </tr>
                        </thead>
                        <tbody>
                            {EXCHANGES.map(ex => (
                                <tr key={ex}>
                                    <td style={td}>{ex.charAt(0).toUpperCase() + ex.slice(1)}</td>
                                    <td style={td}>
                                        <input
                                            type="text"
                                            inputMode="decimal"
                                            value={renderValue(byExchange[ex]?.trading)}
                                            onChange={e => updateFee(ex, "trading", e.target.value)}
                                            placeholder="0.10"
                                            style={feeInput}
                                        />
                                        {byExchange[ex]?.trading?.is_default && <span style={defaultTag}>default</span>}
                                    </td>
                                    <td style={td}>
                                        <input
                                            type="text"
                                            inputMode="decimal"
                                            value={renderValue(byExchange[ex]?.withdrawal)}
                                            onChange={e => updateFee(ex, "withdrawal", e.target.value)}
                                            placeholder="0.05"
                                            style={feeInput}
                                        />
                                        {byExchange[ex]?.withdrawal?.is_default && <span style={defaultTag}>default</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div style={{ marginTop: 16, display: "flex", gap: 10 }}>
                        <button onClick={saveFees} disabled={saving} style={btn("primary")}>
                            {saving ? "Saving..." : "Save fees"}
                        </button>
                        <button onClick={resetFees} disabled={saving} style={btn("secondary")}>
                            Reset to defaults
                        </button>
                    </div>
                </section>

                <section style={card}>
                    <h2 style={h2}>Alert preferences</h2>
                    <p style={hint}>Choose how you want to be notified when profitable spreads appear.</p>

                    <label style={alertLabel}>
                        <input type="checkbox" checked={alerts.email}
                            onChange={e => setAlerts({ ...alerts, email: e.target.checked })}/>
                        <span>Email alerts</span>
                    </label>

                    <label style={alertLabel}>
                        <input type="checkbox" checked={alerts.telegram}
                            onChange={e => setAlerts({ ...alerts, telegram: e.target.checked })}
                            disabled={!telegramLinked}/>
                        <span>
                            Telegram alerts{" "}
                            {!telegramLinked && <em style={{ color: "#6b7280" }}>(connect Telegram first)</em>}
                        </span>
                    </label>

                    <button onClick={saveAlerts} disabled={saving} style={{ ...btn("primary"), marginTop: 16 }}>
                        Save preferences
                    </button>
                </section>

                <section style={card}>
                    <h2 style={h2}>Telegram bot</h2>
                    {telegramLinked ? (
                        <>
                            <p style={{ ...hint, color: "#00ffc6" }}>✓ Connected — you'll receive alerts in Telegram</p>
                            <button onClick={unlinkTelegram} style={btn("secondary")}>Disconnect Telegram</button>
                        </>
                    ) : (
                        <>
                            <p style={hint}>Get instant alerts via Telegram bot @MerabaCryptoBot</p>
                            <button onClick={linkTelegram} style={btn("primary")}>🔗 Connect Telegram</button>
                        </>
                    )}
                </section>
            </div>
        </div>
    )
}

const card = { background: "#0d1433", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: 24, marginBottom: 20 }
const h2   = { color: "#fff", margin: "0 0 8px", fontSize: 18 }
const hint = { color: "#9ca3af", fontSize: 14, margin: "0 0 16px" }
const th   = { textAlign: "left", padding: "8px 12px", color: "#9ca3af", fontWeight: 500, borderBottom: "1px solid rgba(255,255,255,0.08)" }
const td   = { padding: "8px 12px", borderBottom: "1px solid rgba(255,255,255,0.04)" }
const feeInput   = { width: 80, padding: "6px 8px", background: "#060b23", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, color: "#fff", fontFamily: "monospace" }
const defaultTag = { marginLeft: 8, fontSize: 11, color: "#6b7280" }
const alertLabel = { display: "flex", alignItems: "center", gap: 10, padding: "8px 0", cursor: "pointer" }

function btn(kind) {
    return kind === "primary"
        ? { background: "#00ffc6", color: "#060b23", padding: "10px 18px", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600 }
        : { background: "transparent", color: "#cbd5e1", padding: "10px 18px", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 8, cursor: "pointer" }
}
